import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';

import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { SelectItemOptionsType } from 'primereact/selectitem';
import { classNames } from 'primereact/utils';

export type FormDropdownProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  inputClassName?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  options: SelectItemOptionsType | undefined;
  optionLabel?: string;
  optionValue?: string;
  onChange?: (e: DropdownChangeEvent) => void;
  showFilter?: boolean;
  showClear?: boolean;
  required?: boolean;
};

const FormDropdown = <TFieldValues extends FieldValues>({ ...props }: FormDropdownProps<TFieldValues>): JSX.Element => {
  const {
    className,
    inputClassName,
    control,
    field,
    errors,
    label,
    disabled,
    placeholder,
    options,
    optionLabel,
    optionValue,
    onChange,
    showFilter,
    showClear,
    required,
  } = props;

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  return (
    <div className={className ?? 'field'}>
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <label htmlFor={field.name} className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}>
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Dropdown
              id={field.name}
              value={field.value}
              className={classNames(inputClassName, { 'p-invalid': fieldState.error, disabled })}
              disabled={disabled}
              placeholder={placeholder ?? `Select a ${label}`}
              options={options}
              optionLabel={optionLabel}
              optionValue={optionValue}
              onChange={onChange ?? ((e) => field.onChange(e.value))}
              filter={showFilter}
              showClear={showClear}
            />
          </>
        )}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormDropdown;
